import { Link } from "react-router-dom";
import { ArrowRight, Clock, Lightbulb, MapPin, Navigation } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Hero } from "@/components/site/Hero";
import { PageShell } from "@/components/site/PageShell";
import { CtaBand, FaqAccordion, SectionHeading, Steps } from "@/components/sections/Sections";
import { getLocation } from "@/data/areas";
import { STEPS } from "@/data/content";
import { SERVICES } from "@/data/services";
import { ServiceIcon } from "@/lib/icons";
import { breadcrumbLd, faqLd, locationLd } from "@/lib/jsonld";

const InfoCard = ({ icon: Icon, title, text }) => (
  <div className="rounded-2xl border border-slate-200 p-6">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-yellow text-brand-anthracite"><Icon className="h-5 w-5" /></span>
    <h3 className="mt-4 text-lg font-bold text-brand-anthracite">{title}</h3>
    <p className="mt-2 text-[15px] leading-relaxed">{text}</p>
  </div>
);

export default function LocationPage({ location: l }) {
  const crumbs = [{ label: "Einsatzgebiet", to: "/einsatzgebiet" }, { label: l.label, to: `/${l.slug}` }];
  const neighbors = l.neighbors.map(getLocation);

  return (
    <>
      <Seo title={l.title} description={l.description} path={`/${l.slug}`} jsonLd={[locationLd(l), faqLd(l.faq), breadcrumbLd(crumbs)]} />
      <PageShell
        hero={
          <Hero
            image={l.image}
            imageAlt={l.imageAlt}
            imagePosition={l.imagePosition}
            eyebrow={l.kind === "koeln" ? "Einsatzgebiet Köln" : "Einsatzgebiet Umland"}
            title={l.h1}
            subtitle={l.subtitle}
            breadcrumbs={crumbs}
          />
        }
      >
        <div className="space-y-16" data-testid={`location-page-${l.slug}`}>
          <section>
            <p className="eyebrow mb-3">Ihr Gutachter vor Ort</p>
            <h2 className="h2-brand text-2xl sm:text-3xl">Unfall in {l.label}? Wir sind schnell bei Ihnen</h2>
            {l.intro.map((p) => <p key={p} className="mt-4 leading-relaxed">{p}</p>)}
          </section>

          <section className="grid gap-4 sm:grid-cols-2" aria-label="Anfahrt und Einsatzzeit">
            <InfoCard icon={Navigation} title={`Anfahrt nach ${l.label}`} text={l.arrival} />
            <InfoCard icon={Clock} title="Einsatzzeit" text={l.time} />
          </section>

          <section>
            <SectionHeading eyebrow="Vor Ort" title={`Straßen & typische Einsatzorte in ${l.label}`} intro={l.streets.text} />
            <ul className="grid gap-3 sm:grid-cols-2" data-testid="location-streets">
              {l.streets.items.map((i) => (
                <li key={i} className="flex gap-3 rounded-2xl bg-brand-mist p-4 text-[15px] font-semibold text-brand-anthracite">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue" /> {i}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-3xl border-l-4 border-brand-yellow bg-white p-6 shadow-soft ring-1 ring-slate-100 sm:p-8">
            <Lightbulb className="h-7 w-7 text-brand-blue" />
            <h2 className="mt-3 text-2xl font-bold text-brand-anthracite">{l.extra.heading}</h2>
            <p className="mt-3 leading-relaxed">{l.extra.text}</p>
          </section>

          <section>
            <SectionHeading eyebrow="Gut zu wissen" title={`Gut zu wissen für Autofahrer aus ${l.label}`} />
            {l.more.map((p) => <p key={p} className="mt-4 leading-relaxed first:mt-0">{p}</p>)}
          </section>

          <section>
            <SectionHeading eyebrow="Leistungen" title={`Unsere Leistungen in ${l.label}`} />
            <ul className="grid gap-3 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} data-testid={`location-service-${s.slug}`}
                    className="group flex items-center gap-3 rounded-2xl border border-slate-200 p-4 font-semibold text-brand-anthracite transition-[border-color,color] hover:border-brand-blue hover:text-brand-blue">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-mist text-brand-blue"><ServiceIcon name={s.icon} className="h-5 w-5" /></span>
                    <span className="flex-1">{s.name}</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <SectionHeading eyebrow="Ablauf" title="In 4 Schritten zu Ihrem Geld" />
            <Steps items={STEPS} prefix="location-steps" />
          </section>

          <section>
            <SectionHeading eyebrow="FAQ" title={`Häufige Fragen aus ${l.label}`} />
            <FaqAccordion items={l.faq} prefix="location" />
          </section>

          <section>
            <SectionHeading eyebrow="In der Nähe" title="Auch in diesen Nachbarorten für Sie da" />
            <ul className="grid gap-3 sm:grid-cols-3" data-testid="location-neighbors">
              {neighbors.map((n) => (
                <li key={n.slug}>
                  <Link to={`/${n.slug}`} data-testid={`neighbor-link-${n.slug}`}
                    className="group flex h-full flex-col justify-between rounded-2xl bg-brand-blue p-5 text-white transition-transform hover:-translate-y-1">
                    <MapPin className="h-5 w-5 text-brand-yellow" />
                    <span className="mt-4 block font-bold">Kfz-Gutachter {n.label}</span>
                    <span className="mt-1 inline-flex items-center gap-1 text-sm text-white/80">Mehr erfahren <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <CtaBand prefix="location-cta" />
        </div>
      </PageShell>
    </>
  );
}
